import type { SocketMessage } from "~/types";
import { isSystemOnlyMessage, isSystemMessage } from "~/utils/chat";
import { useChatStore } from "~/stores/chat/store";
import { useAuthStore } from "~/stores/auth/store";
import { consola } from "consola";

interface UseVideoChatProps {
  videoId: string;
}
export default function (props: UseVideoChatProps) {
  const config = useRuntimeConfig();

  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);

  const chatStore = useChatStore();
  const { messages, chatEnabled, chatStatus, tabDisabled } =
    storeToRefs(chatStore);

  const connection = ref<WebSocket | null>(null);
  const readyState = ref<number | undefined>(undefined);

  const periodicPingInterval = ref<ReturnType<typeof setInterval>>();

  async function initChat() {
    tabDisabled.value = false;
    await chatStore.initChat(props.videoId);
    establishSocketConnection();
  }

  function initPeriodicPing() {
    periodicPingInterval.value = setInterval(() => {
      if (connection.value && readyState.value === 1) {
        connection.value.send(JSON.stringify({ ping: "" }));
      }
    }, 30_000);
  }

  function establishSocketConnection() {
    const accessToken = useCookie("access_token");

    connection.value = new WebSocket(
      `${config.public.chatSocketAddress}/v1/api/video/${
        props.videoId
      }/token/${accessToken.value}/chat`,
    );

    readyState.value = connection.value.readyState;

    connection.value.onopen = () => {
      readyState.value = connection.value?.readyState;
      consola.success("[WebSocket] Connected.");
      initPeriodicPing();
    };

    connection.value.onclose = (reason) => {
      readyState.value = connection.value?.readyState;
      clearInterval(periodicPingInterval.value);
      connection.value = null;
      consola.warn("[WebSocket] closed.");
    };

    connection.value.onmessage = (event) => {
      readyState.value = connection.value?.readyState;
      const msg = JSON.parse(event.data);
      if (isSystemOnlyMessage(msg)) {
        return;
      }
      if (isSystemMessage(msg)) {
        handleSystemMessage(msg);
      } else {
        handleUserMessage(msg);
      }
    };
  }

  onBeforeUnmount(() => {
    if (connection.value) {
      clearInterval(periodicPingInterval.value);
      connection.value.close();
    }
  });

  function handleUserMessage(socketMessage: SocketMessage) {
    if (!socketMessage.content.hidden) {
      messages.value.push(socketMessage);
    }
  }

  function handleSystemMessage(socketMessage: SocketMessage) {
    let appendToMessages = true;

    switch (socketMessage.content.code) {
      case "hide": {
        chatEnabled.value = false;
        break;
      }
      case "show": {
        chatEnabled.value = true;
        break;
      }
      case "retry":
      case "login_from_other": {
        // this.socketClosed = true;
        // this.highlight(socketMessage.content.message);
        tabDisabled.value = true;
        break;
      }
      case "join": {
        // this.participantsShown.push(socketMessage.content.meta_info?.username);
        // this.participantsCountShown += 1;
        break;
      }
      case "left": {
        // this.participantsShown = this.participantsShown.filter(
        //   (p) => p !== socketMessage.content.meta_info?.username,
        // );
        // this.participantsCountShown -= 1;
        break;
      }
      case "paused": {
        // this.highlight(socketMessage.content.message);
        chatStatus.value = "paused";
        break;
      }
      case "started": {
        // this.highlight(socketMessage.content.message);
        chatStatus.value = "live";
        break;
      }
      case "live": {
        // this.highlight(socketMessage.content.message);
        chatStatus.value = "live";
        break;
      }
      case "ended": {
        chatStatus.value = "ended";
        // this.socketClosed = true;
        // this.highlight(socketMessage.content.message);
        break;
      }
      case "ban_user": {
        appendToMessages = false;
        const bannedUser = socketMessage.content.meta_info?.username.trim();
        if (bannedUser === user.value.username) {
          socketMessage.content.message += new Date(
            socketMessage.content.expires,
          ).toLocaleString();
          messages.value.push(socketMessage);
        }
        break;
      }
      case "unban_user": {
        appendToMessages = false;
        const bannedUser = socketMessage.content.meta_info?.username.trim();
        if (bannedUser === user.value.username) {
          messages.value.push(socketMessage);
        }
        break;
      }
      case "report": {
        appendToMessages = false;
        const reportingUser =
          socketMessage.content.meta_info?.reporting_username.trim();
        if (reportingUser === user.value.username) {
          messages.value.push(socketMessage);
        }
        break;
      }
      case "block_message": {
        appendToMessages = false;
        const messageId = socketMessage.content.meta_info?.message_id.trim();
        if (messageId) {
          messages.value = messages.value.filter(
            (message) => message.id !== messageId,
          );
        }
        break;
      }
    }

    if (appendToMessages && !socketMessage.content.hidden) {
      messages.value.push(socketMessage);
    }
  }

  return { initChat, connection, readyState };
}
